import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BiCheckCircle, BiDownload, BiExpand, BiLogoLinkedinSquare, BiLogoYoutube, BiPlay, BiStore } from 'react-icons/bi';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { CustomReactMarkdown } from '@/components/common/CustomReactMarkdown';
import ProBadge from '@/components/ui/ProBadge';
import { resetCreateFlow, toggeleCreateModal } from '@/features/scenario/create/create.slice';

export interface TemplateCardData {
  version?: string;
  price?: number;
  name?: string;
  type?: string;
  package_id?: string;
  package_public_name?: string;
  configuration?: {
    bots?: string[];
  };
  data?: {
    description?: string;
    image?: string;
    youtube_link?: string;
    linkedin_link?: string;
    delivery_delay?: number;
    promotional_video?: string;
  };
  id?: number | string;
  code?: string;
  title?: { [key: string]: string } | string;
  description?: { [key: string]: string } | string;
  icon?: string;
  image?: string;
  publisher?: {
    name: string;
    icon: string;
  };
  hasRequiredPermission?: boolean;
  downloadCount?: number;
}

export interface TemplateCardProps {
  item: TemplateCardData;
  isSelected: boolean;
  isDisabled?: boolean;
  isInEditMode?: boolean;
  showOverlay?: boolean;
  onClick: (item: TemplateCardData) => void;
  onKeyDown?: (item: TemplateCardData) => void;
  className?: string;
  imageClassName?: string;
  variant?: 'default' | 'quickbot';
  canCreateMore?: boolean;
  disableAction?: boolean;
}

// Smart Video Player Component
interface VideoPlayerProps {
  videoSrc: string;
  title: string;
  canInteract: boolean;
  isCardHovered: boolean;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ videoSrc, title, canInteract, isCardHovered }) => {
  const { t } = useTranslation();
  const [videoState, setVideoState] = useState<'thumbnail' | 'inline' | 'modal'>('thumbnail');
  const [isVideoHovered, setIsVideoHovered] = useState(false);

  const handlePlayClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (videoState === 'thumbnail') {
      setVideoState('inline');
    }
  };

  const handleFullScreenClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setVideoState('modal');
  };

  const handleCloseModal = () => {
    setVideoState('thumbnail');
  };

  const handleVideoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <>
      <div
        className="tw-relative tw-w-full"
        style={{ aspectRatio: '9/16' }}
        onMouseEnter={() => setIsVideoHovered(true)}
        onMouseLeave={() => setIsVideoHovered(false)}
      >
        {videoState === 'thumbnail' ? (
          /* Video Thumbnail with Play Button */
          <>
            <video
              src={videoSrc}
              className="tw-w-full tw-h-full tw-object-cover"
              preload="metadata"
              muted
              aria-label={`${t('TemplateCard/videoPreview')} ${title}`}
            />

            {/* Play Button Overlay - Always Visible */}
            <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-black/20">
              <button
                id="btn-play"
                onClick={handlePlayClick}
                className="tw-w-16 tw-h-16 tw-bg-white/90 hover:tw-bg-white tw-rounded-full tw-flex tw-items-center tw-justify-center tw-transition-all tw-duration-200 tw-transform hover:tw-scale-110 tw-shadow-lg"
                aria-label={`${t('TemplateCard/playVideo')} ${title}`}
              >
                <BiPlay className="tw-w-8 tw-h-8 tw-text-gray-800 tw-ml-1" />
              </button>
            </div>

            {/* Video Controls Overlay - Shows on Hover */}
            {isVideoHovered && canInteract && (
              <div className="tw-absolute tw-top-3 tw-right-3 tw-flex tw-gap-2">
                <button
                  id="btn-full-screen"
                  onClick={handleFullScreenClick}
                  className="tw-w-8 tw-h-8 tw-bg-black/60 hover:tw-bg-black/80 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-transition-all tw-duration-200"
                  aria-label={t('TemplateCard/fullScreen')}
                >
                  <BiExpand className="tw-w-4 tw-h-4 tw-text-white" />
                </button>
              </div>
            )}

            <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-black/20 tw-via-transparent tw-to-transparent tw-pointer-events-none" />
          </>
        ) : (
          /* Inline Video Player */
          <>
            <video
              src={videoSrc}
              className="tw-w-full tw-h-full tw-object-cover"
              controls
              autoPlay
              preload="metadata"
              onClick={handleVideoClick}
              aria-label={`${t('TemplateCard/playingVideo')} ${title}`}
              onError={() => {
                setVideoState('thumbnail');
              }}
            >
              <track kind="captions" />
            </video>

            {/* Close Button */}
            <button
              id="btn-close-thumbnail"
              onClick={e => {
                e.stopPropagation();
                setVideoState('thumbnail');
              }}
              className="tw-absolute tw-top-3 tw-right-3 tw-w-8 tw-h-8 tw-bg-black/60 hover:tw-bg-black/80 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-transition-all tw-duration-200 tw-z-10"
              aria-label={t('TemplateCard/closeVideo')}
            >
              <span className="tw-text-white tw-text-lg tw-leading-none">×</span>
            </button>

            <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-black/20 tw-via-transparent tw-to-transparent tw-pointer-events-none" />
          </>
        )}
      </div>

      {/* Full Screen Modal */}
      {videoState === 'modal' && (
        <div className="tw-fixed tw-inset-0 tw-bg-black/90 tw-z-[9999] tw-flex tw-items-center tw-justify-center tw-p-4">
          <div className="tw-relative tw-w-full tw-max-w-4xl">
            {/* Close Button */}
            <button
              id="btn-close-modal"
              onClick={handleCloseModal}
              className="tw-absolute -tw-top-12 tw-right-0 tw-w-10 tw-h-10 tw-bg-white/20 hover:tw-bg-white/30 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-transition-all tw-duration-200"
              aria-label={t('TemplateCard/closeModal')}
            >
              <span className="tw-text-white tw-text-xl tw-leading-none">×</span>
            </button>

            {/* Video Container */}
            <div className="tw-relative tw-w-full" style={{ aspectRatio: '16/9' }}>
              <video
                src={videoSrc}
                className="tw-w-full tw-h-full tw-object-contain tw-bg-black tw-rounded-lg"
                controls
                autoPlay
                preload="metadata"
                aria-label={`${t('TemplateCard/fullScreenVideo')} ${title}`}
              >
                <track kind="captions" />
              </video>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const TemplateCard: React.FC<TemplateCardProps> = ({
  item,
  isSelected,
  isDisabled = false,
  isInEditMode = false,
  showOverlay = false,
  onClick,
  onKeyDown,
  className = '',
  imageClassName = '',
  variant = 'default',
  canCreateMore = false,
  disableAction = false,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isHovered, setIsHovered] = useState(false);
  const [isContentHovered, setIsContentHovered] = useState(false);
  const [showHoverOverlay, setShowHoverOverlay] = useState(false);
  const [showVideoOverlay, setShowVideoOverlay] = useState(false);

  const getTitle = useMemo(() => {
    if (item.name) return item.name;
    if (item.package_public_name) return item.package_public_name;

    if (typeof item.title === 'string') return item.title;
    if (typeof item.title === 'object') return item.title[i18n.language] || item.title['en'];
    return '';
  }, [item.name, item.package_public_name, item.title, i18n.language]);

  const getDescription = useMemo(() => {
    if (item.data?.description) return item.data.description;
    if (typeof item.description === 'string') return item.description;
    if (typeof item.description === 'object') return item.description[i18n.language] || item.description['en'];
    return '';
  }, [item.data?.description, item.description, i18n.language]);

  const getImageSrc = useMemo(() => {
    if (item.data?.image) return item.data.image;
    if (item.icon) return item.icon;
    if (item.image) return item.image;
    return '';
  }, [item.data?.image, item.icon, item.image]);

  const formatNumber = useCallback((num?: number) => {
    if (!num) return '0';
    if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
    return num.toString();
  }, []);

  const handleClick = useCallback(() => {
    if (!isDisabled && !isInEditMode && !disableAction) {
      onClick(item);
    }
  }, [isDisabled, isInEditMode, disableAction, onClick, item]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.key === 'Enter' || e.key === ' ') && !isDisabled && !isInEditMode && !disableAction) {
        e.preventDefault();
        if (onKeyDown) {
          onKeyDown(item);
        } else {
          onClick(item);
        }
      }
    },
    [isDisabled, isInEditMode, disableAction, onKeyDown, onClick, item]
  );

  const canInteract = ((!isDisabled && !isInEditMode && !disableAction) || canCreateMore) && !disableAction;

  // Hover overlay logic: Show when content is hovered, hide only when mouse leaves entire card
  useEffect(() => {
    if (isContentHovered) {
      setShowHoverOverlay(true);
    } else if (!isHovered) {
      setShowHoverOverlay(false);
    }
  }, [isContentHovered, isHovered]);

  const baseClasses = `tw-group tw-relative tw-w-full tw-h-full tw-bg-white tw-border tw-border-gray-200 tw-rounded-xl tw-overflow-hidden tw-transition-all tw-duration-300 tw-ease-in-out tw-shadow-lg tw-flex tw-flex-col`;

  const hoverClasses = canInteract ? 'hover:tw-shadow-xl hover:tw-border-blue-300 hover:-tw-translate-y-1' : '';

  const selectedClasses = isSelected ? 'tw-ring-2 tw-ring-blue-500 tw-border-blue-500 tw-shadow-lg' : '';

  const cursorClasses = canInteract ? 'tw-cursor-pointer' : 'tw-cursor-not-allowed';

  const visibilityClasses = isInEditMode && !isSelected ? 'tw-opacity-50' : '';

  const cardClasses = `${baseClasses} ${hoverClasses} ${selectedClasses} ${cursorClasses} ${visibilityClasses} ${className}`;

  return (
    <div
      className={cardClasses}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="button"
      id="btn-template-card-selector"
      aria-label="template-selector"
      tabIndex={0}
    >
      {/* MAIN CONTENT WRAPPER - Image + Description combined 9:16 aspect ratio */}
      <div className="tw-relative tw-flex tw-flex-col" style={{ aspectRatio: '9/16' }}>
        {/* SECTION 1: IMAGE SECTION - Fixed 16:9 aspect ratio */}
        <div className="tw-relative tw-overflow-hidden tw-flex-shrink-0">
          <div
            className="tw-relative tw-w-full tw-bg-gray-100"
            onClick={
              item.data?.promotional_video
                ? e => {
                    e.stopPropagation();
                    setShowVideoOverlay(true);
                  }
                : undefined
            }
            onKeyDown={
              item.data?.promotional_video
                ? e => {
                    if (e.key === 'Enter') {
                      e.stopPropagation();
                      setShowVideoOverlay(true);
                    }
                  }
                : undefined
            }
            role={item.data?.promotional_video ? 'button' : undefined}
            tabIndex={item.data?.promotional_video ? 0 : undefined}
            style={{
              aspectRatio: '16/9',
              cursor: item.data?.promotional_video ? 'pointer' : 'default',
            }}
          >
            <img
              src={getImageSrc}
              alt={getTitle}
              loading="lazy"
              className={`tw-w-full tw-h-full tw-object-cover tw-transition-transform tw-duration-300 ${
                isHovered && canInteract ? 'tw-scale-105' : ''
              } ${imageClassName}`}
              onError={e => {
                e.currentTarget.style.display = 'block';
              }}
            />
            {/* Gradient Overlay for Better Text Readability */}
            <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-black/20 tw-via-transparent tw-to-transparent" />

            {/* Play Button Overlay - Only show if we have promotional video */}
            {item.data?.promotional_video && (
              <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-black/20">
                <div className="tw-w-16 tw-h-16 tw-bg-white/90 hover:tw-bg-white tw-rounded-full tw-flex tw-items-center tw-justify-center tw-transition-all tw-duration-200 tw-transform hover:tw-scale-110 tw-shadow-lg">
                  <BiPlay className="tw-w-8 tw-h-8 tw-text-gray-800 tw-ml-1" />
                </div>
              </div>
            )}

            {/* Fallback if no image */}
            {!getImageSrc && (
              <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-gray-200">
                <span className="tw-text-gray-500 tw-text-sm">{t('TemplateCard/noImage')}</span>
              </div>
            )}
          </div>
        </div>

        {/* SECTION 2: DESCRIPTION SECTION - Flexible height to fill remaining 9:16 space */}
        <div className="tw-flex tw-flex-col tw-flex-1 tw-p-4 tw-relative">
          <div className="tw-flex-1 tw-space-y-3" onMouseEnter={() => setIsContentHovered(true)} onMouseLeave={() => setIsContentHovered(false)}>
            {/* Title and Category */}
            <div className="tw-space-y-2">
              <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                <h3 className="tw-font-semibold tw-text-gray-900 tw-text-base tw-line-clamp-2 tw-leading-tight tw-font-['Lexend']">{getTitle}</h3>
              </div>

              {/* Empty Space for Better Layout */}
              <div className="tw-flex tw-items-center tw-justify-between tw-pt-4">
                <div className="tw-flex tw-flex-col tw-items-start tw-gap-6 tw-text-sm ">
                  {item.version && (
                    <div className="tw-bg-gray-100 tw-text-gray-700 tw-px-2 tw-py-0.5 tw-rounded-md tw-font-medium">
                      {t('TemplateCard/version')}: {item.version}
                    </div>
                  )}
                  {item.configuration?.bots && item.configuration.bots.length > 0 && (
                    <div className="tw-bg-blue-100 tw-text-blue-700 tw-px-2 tw-py-0.5 tw-rounded-md tw-font-medium">
                      {t('TemplateCard/includes')} {item.configuration.bots.length}{' '}
                      {item.configuration.bots.length > 1 ? t('TemplateCard/bots') : t('TemplateCard/bot')}
                    </div>
                  )}
                  {item.data?.delivery_delay && (
                    <div className="tw-bg-green-100 tw-text-green-700 tw-px-2 tw-py-0.5 tw-rounded-md tw-font-medium">
                      {t('TemplateCard/sellerDelivery')}: {item.data?.delivery_delay}{' '}
                      {item.data?.delivery_delay === 1 ? t('TemplateCard/day') : t('TemplateCard/days')}
                    </div>
                  )}
                </div>
              </div>

              {/* Stats and Badges */}
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-pt-4">
                <div className="tw-flex tw-items-center tw-gap-3">
                  {item.downloadCount > 0 && (
                    <div className="tw-flex tw-items-center tw-gap-1 tw-text-md tw-text-gray-600">
                      <BiDownload size={20} />
                      <span>{formatNumber(item.downloadCount)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Pricing Section - Fixed at Bottom of Description Section */}
          <div className="tw-mt-auto">
            {item.price !== undefined ? (
              <div className="tw-bg-gradient-to-r tw-from-blue-50 tw-to-indigo-50 tw-rounded-xl tw-p-3 tw-border tw-border-blue-200 tw-min-h-[70px]">
                <div className="tw-flex tw-items-center tw-justify-between">
                  <div className="tw-flex tw-flex-col">
                    <div className="tw-flex tw-items-baseline tw-gap-2">
                      <span className="tw-text-xl tw-font-black tw-text-gray-900">{item.price === 0 ? t('TemplateCard/free') : `$${item.price}`}</span>
                    </div>
                    {item.price > 0 && (
                      <div className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-600 tw-mt-0.5">
                        <BiStore className="tw-w-3 tw-h-3" />
                        <span>{t('TemplateCard/oneTimePurchase')}</span>
                      </div>
                    )}
                  </div>
                  {isDisabled && <ProBadge variant="large" icon={null} hasCreationBotLimitReached={true} className="tw-w-6 tw-h-6 tw-rounded-full tw-shadow" />}
                </div>
              </div>
            ) : (
              <div className="tw-bg-gradient-to-r tw-from-green-50 tw-to-emerald-50 tw-rounded-xl tw-p-3 tw-border tw-border-green-200 tw-min-h-[70px]">
                <div className="tw-flex tw-items-center tw-justify-between">
                  <span className="tw-text-xl tw-font-black tw-text-green-700">{t('TemplateCard/free')}</span>
                  {isDisabled && <ProBadge icon={null} hasCreationBotLimitReached={true} className="tw-w-6 tw-h-6 tw-rounded-full tw-shadow" />}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Edit Mode Overlay */}
        {showOverlay && isInEditMode && !isSelected && <div className="tw-absolute tw-inset-0 tw-bg-gray-200/50 tw-backdrop-blur-[1px]" />}
      </div>

      {/* SECTION 3: FOOTER SECTION - Outside the 9:16 ratio */}
      <div className="tw-flex-shrink-0 tw-p-4 tw-border-t tw-border-gray-100">
        <div className="tw-flex tw-items-center tw-justify-between">
          <div className="tw-flex tw-items-center tw-gap-2">
            {/* Icon Logic: Louise icon for Louise, Store icon for marketplace */}
            {item.publisher?.name && item.publisher?.name.toLowerCase() !== 'louise' ? (
              <BiStore className="tw-w-5 tw-h-5 tw-text-gray-600" />
            ) : (
              <img src="https://cdn.1cdn.app/application/LOUISEBOT/23091707385795_Logo_colorful.svg" alt="Louise" className="tw-w-5 tw-h-5 tw-rounded-full" />
            )}

            {/* Author Name */}
            {item.publisher?.name && item.publisher?.name.toLowerCase() === 'louise' && (
              <span className="tw-text-gray-600 tw-text-sm tw-font-medium">
                {t('TemplateCard/by')} {item.publisher.name}
              </span>
            )}
          </div>

          {/* Social Links & Trust indicators */}
          {(item.publisher?.name || !canInteract) && (
            <div className="tw-flex tw-items-center tw-gap-3">
              {/* Social Links */}
              <div className="tw-flex tw-items-center tw-gap-1">
                {/* LinkedIn Link */}
                {item.data?.linkedin_link && (
                  <a
                    href={item.data?.linkedin_link}
                    target="_blank"
                    rel="noreferrer"
                    className="tw-flex tw-items-center tw-text-blue-600 hover:tw-text-blue-700 tw-transition-colors"
                    onClick={e => e.stopPropagation()}
                  >
                    <BiLogoLinkedinSquare className="tw-w-6 tw-h-6" />
                  </a>
                )}

                {/* YouTube Link */}
                {item.data?.youtube_link && (
                  <a
                    href={item.data.youtube_link}
                    target="_blank"
                    rel="noreferrer"
                    className="tw-flex tw-items-center tw-text-red-600 hover:tw-text-red-700 tw-transition-colors tw-ml-1"
                    onClick={e => e.stopPropagation()}
                  >
                    <BiLogoYoutube className="tw-w-6 tw-h-6" />
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Full Card Hover Overlay with Description and Actions - Covers Only 9:16 Wrapper */}
      {showHoverOverlay && (
        <div
          className="tw-absolute tw-inset-0 tw-bg-gray-900 tw-flex tw-flex-col tw-opacity-100 tw-transition-opacity tw-duration-300 tw-p-6 tw-z-10"
          style={{ aspectRatio: '9/16' }}
        >
          <div className="tw-flex-1 tw-overflow-y-auto tw-space-y-4">
            {/* Title */}
            <h4 className="tw-font-bold tw-text-white tw-text-lg tw-font-['Lexend']">{getTitle}</h4>

            {/* Full Description in Markdown */}
            <div className="tw-text-gray-200 tw-text-sm tw-leading-relaxed">
              {item.data?.description ? <CustomReactMarkdown content={item.data.description} className="tw-text-sm" /> : <p>{getDescription}</p>}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="tw-mt-4 tw-space-y-3">
            {isDisabled ? (
              <div className="tw-space-y-2">
                {/* Upgrade Button with Price */}
                <button
                  id="btn-upgrade"
                  type="button"
                  disabled={disableAction}
                  className={`tw-w-full tw-relative tw-overflow-hidden tw-bg-gradient-to-r tw-from-violet-500 tw-via-purple-500 tw-to-fuchsia-400 hover:tw-from-violet-600 hover:tw-via-purple-600 hover:tw-to-fuchsia-500 tw-text-white tw-font-semibold tw-py-4 tw-px-6 tw-rounded-xl tw-shadow-lg tw-shadow-purple-500/30 hover:tw-shadow-purple-500/50 tw-transition-all tw-duration-300 tw-transform hover:tw-scale-[1.02] ${
                    disableAction ? 'tw-opacity-50 tw-cursor-not-allowed' : ''
                  }`}
                  onClick={e => {
                    if (disableAction) return;
                    navigate('billing-create');
                    dispatch(resetCreateFlow());
                    dispatch(toggeleCreateModal(false));
                  }}
                >
                  <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-relative tw-z-10">
                    <span className="tw-text-sm tw-tracking-wide">{t('TemplateCard/upgradeToAccess')}</span>
                    <span className="tw-px-3 tw-py-1 tw-bg-white/20 tw-rounded-lg tw-text-sm tw-font-bold">PRO</span>
                  </div>

                  {/* Subtle Shine Effect */}
                  <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-r tw-from-transparent tw-via-white/20 tw-to-transparent tw-translate-x-[-100%] group-hover:tw-translate-x-[100%] tw-transition-transform tw-duration-700"></div>
                </button>
              </div>
            ) : item.price !== undefined && item.price > 0 ? (
              <div className="tw-space-y-2">
                {/* Buy Button with Price */}
                <button
                  id="btn-buy-price"
                  disabled={disableAction}
                  className={`tw-w-full tw-relative tw-overflow-hidden tw-bg-gradient-to-r tw-from-indigo-500 tw-via-blue-500 tw-to-cyan-400 hover:tw-from-indigo-600 hover:tw-via-blue-600 hover:tw-to-cyan-500 tw-text-white tw-font-semibold tw-py-4 tw-px-6 tw-rounded-xl tw-shadow-lg tw-shadow-blue-500/30 hover:tw-shadow-blue-500/50 tw-transition-all tw-duration-300 tw-transform hover:tw-scale-[1.02] ${
                    disableAction ? 'tw-opacity-50 tw-cursor-not-allowed' : ''
                  }`}
                  onClick={e => {
                    if (disableAction) return;
                    e.stopPropagation();
                    handleClick();
                  }}
                >
                  <div className="tw-flex tw-items-center tw-justify-center tw-gap-3 tw-relative tw-z-10">
                    <span className="tw-text-base tw-tracking-wide">{t('TemplateCard/buyNow')}</span>
                    <span className="tw-px-3 tw-py-1 tw-bg-white/20 tw-rounded-lg tw-text-lg tw-font-bold">${item.price}</span>
                  </div>

                  {/* Subtle Shine Effect */}
                  <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-r tw-from-transparent tw-via-white/20 tw-to-transparent tw-translate-x-[-100%] group-hover:tw-translate-x-[100%] tw-transition-transform tw-duration-700"></div>
                </button>
              </div>
            ) : (
              /* Free Template */
              <button
                id="btn-free-template"
                disabled={disableAction}
                className={`tw-w-full tw-bg-gradient-to-r tw-from-emerald-500 tw-to-emerald-600 hover:tw-from-emerald-600 hover:tw-to-emerald-700 tw-text-white tw-font-bold tw-py-4 tw-px-6 tw-rounded-xl tw-shadow-xl hover:tw-shadow-emerald-500/25 tw-transition-all tw-duration-300 tw-transform hover:tw-scale-105 ${
                  disableAction ? 'tw-opacity-50 tw-cursor-not-allowed' : ''
                }`}
                onClick={e => {
                  if (disableAction) return;
                  e.stopPropagation();
                  handleClick();
                }}
              >
                <div className="tw-flex tw-items-center tw-justify-center tw-gap-3">
                  <BiCheckCircle className="tw-w-6 tw-h-6" />
                  <span className="tw-text-md">{t('TemplateCard/getFreeTemplate')}</span>
                  <span>✨</span>
                </div>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Video Overlay - Covers Only 9:16 Wrapper */}
      {showVideoOverlay && item.data?.promotional_video && (
        <div className="tw-absolute tw-inset-0 tw-bg-black tw-flex tw-flex-col tw-z-20" style={{ aspectRatio: '9/16' }}>
          {/* Close Button - Enhanced Visibility */}
          <button
            id="btn-close-video-overlay"
            onClick={e => {
              e.stopPropagation();
              setShowVideoOverlay(false);
            }}
            className="tw-absolute tw-top-4 tw-right-4 tw-w-10 tw-h-10 tw-bg-black/50 hover:tw-bg-black/70 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-transition-all tw-duration-200 tw-z-30"
            aria-label={t('TemplateCard/closeVideo')}
          >
            <span className="tw-text-white tw-text-xl tw-font-bold tw-leading-none">×</span>
          </button>

          {/* Video Container - Perfect 9:16 Integration */}
          <video
            src={item.data?.promotional_video}
            className="tw-w-full tw-h-full tw-object-cover"
            controls
            autoPlay
            preload="metadata"
            onClick={e => e.stopPropagation()}
            aria-label={`${t('TemplateCard/promotionalVideo')} ${getTitle}`}
            onError={() => {
              setShowVideoOverlay(false);
            }}
          >
            <track kind="captions" />
          </video>
        </div>
      )}
    </div>
  );
};

export default TemplateCard;
